import React from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Badge,
  useColorModeValue,
  useToast,
  Tooltip,
  Select,
  Text,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  VStack,
  SimpleGrid,
  Skeleton,
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { DataTable } from 'components';
import { Api } from '../api/api';
import { useAuthUser } from '../core/hooks';
import { OrderStatus, OrderStatusLabel, OrderListItemResponse, OrderDetailResponse } from '../types/order.types';
import { OrderDetail } from './OrderDetail';

const statusColors: Record<OrderStatus, string> = {
  [OrderStatus.PROCESSING]: 'blue',
  [OrderStatus.SHIPPED]: 'purple',
  [OrderStatus.DELIVERED]: 'green',
  [OrderStatus.CANCELLED]: 'red',
  [OrderStatus.RETURNED]: 'orange',
};

export const Orders: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const authUser = useAuthUser();
  const isAdmin = authUser?.isAdmin || authUser?.isSuperAdmin;
  const bgColor = useColorModeValue('gray.100', '#0B192C');
  const cardBgColor = useColorModeValue('white', '#1E3E62');
  const textColorPrimary = useColorModeValue('gray.800', 'whiteAlpha.900');
  const borderColor = useColorModeValue('gray.200', '#4A5568');
  const priceColor = useColorModeValue('orange.300', '#FF6500');
  const highlightColorScheme = 'orange';
  const [orderDetail, setOrderDetail] = React.useState<OrderDetailResponse | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = React.useState(false);

  const { data: orders } = useQuery<OrderListItemResponse[], Error>(
    'orders',
    async () => {
      const response = await Api.Order.getOrderList();
      return response.data.orders;
    },
    {
      onError: () => {
        toast({
          title: 'Hata',
          description: 'Siparişler yüklenirken bir sorun oluştu.',
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      },
    },
  );

  const handleStatusChange = async (orderId: number, newStatus: OrderStatus) => {
    try {
      await Api.Order.updateOrderStatus(orderId.toString(), newStatus.toString());
      queryClient.invalidateQueries('orders');
      toast({
        title: 'Başarılı',
        description: 'Sipariş durumu güncellendi.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Sipariş durumu güncellenirken bir sorun oluştu.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleRowClick = async (orderId: string | number) => {
    try {
      setIsLoadingDetail(true);
      const response = await Api.Order.getOrder(orderId.toString());
      setOrderDetail(response.data);
    } catch (e) {
      toast({
        title: 'Detay Yüklenemedi',
        description: 'Sipariş detayı alınırken bir hata oluştu.',
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const columns = [
    {
      header: 'Sipariş No',
      accessorKey: 'orderId',
    },
    {
      header: 'Tarih',
      accessorKey: 'createdAt',
      cell: (info: any) => new Date(info.getValue()).toLocaleDateString('tr-TR'),
    },
    ...(isAdmin
      ? [
          {
            header: 'Bayi',
            accessorKey: 'expertisePointName',
            cell: (info: any) => (
              <Tooltip label={info.getValue()}>
                <Text>{info.getValue()}</Text>
              </Tooltip>
            ),
          },
          {
            header: 'Bayi ID',
            accessorKey: 'expertisePointId',
          },
        ]
      : []),
    {
      header: 'Müşteri',
      accessorKey: 'userName',
      cell: (info: any) => (
        <Tooltip label={info.getValue()}>
          <Text>{info.getValue()}</Text>
        </Tooltip>
      ),
    },
    ...(isAdmin
      ? [
          {
            header: 'Müşteri ID',
            accessorKey: 'userId',
          },
        ]
      : []),
    {
      header: 'Tutar',
      accessorKey: 'totalPrice',
      cell: (info: any) => info.getValue().toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' }),
    },
    {
      header: 'Durum',
      accessorKey: 'status',
      cell: (info: any) => {
        const status = info.getValue() as OrderStatus;
        return (
          <Badge colorScheme={statusColors[status]} variant="solid" px={2} py={1} borderRadius="md">
            {OrderStatusLabel.get(status)}
          </Badge>
        );
      },
    },
    ...(isAdmin
      ? [
          {
            header: 'İşlemler',
            accessorKey: 'orderId',
            cell: (info: any) => {
              const order = orders?.find((o) => o.orderId === info.getValue());
              if (!order) return null;
              return (
                <Select
                  value={order.status}
                  onChange={(e) => handleStatusChange(order.orderId, Number(e.target.value) as OrderStatus)}
                  size="sm"
                  width="150px"
                >
                  {Array.from(OrderStatusLabel.entries()).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              );
            },
          },
        ]
      : []),
    {
      header: 'Detay',
      accessorKey: 'orderId',
      cell: (info: any) => (
        <Button
          size="sm"
          colorScheme={highlightColorScheme}
          variant="outline"
          onClick={() => handleRowClick(info.getValue())}
        >
          Detay
        </Button>
      ),
    },
  ];

  const renderModalContent = () => {
    if (isLoadingDetail) {
      return (
        <>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} mb={8}>
            <VStack align="stretch" spacing={4}>
              <Skeleton height="20px" />
              <Skeleton height="20px" />
              <Skeleton height="20px" />
              <Skeleton height="20px" />
              <Skeleton height="20px" />
            </VStack>
            <VStack align="stretch" spacing={4}>
              <Skeleton height="20px" />
              <Skeleton height="20px" />
              <Skeleton height="20px" />
            </VStack>
          </SimpleGrid>
          <Box mb={6}>
            <Skeleton height="24px" mb={4} />
            <VStack spacing={4} align="stretch">
              {[1, 2, 3].map((i) => (
                <Flex
                  key={i}
                  align="center"
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  borderColor={borderColor}
                  bg={cardBgColor}
                >
                  <Box flex="1">
                    <Skeleton height="20px" mb={2} />
                    <Skeleton height="16px" mb={2} />
                    <Skeleton height="16px" />
                  </Box>
                  <Box textAlign="right">
                    <Skeleton height="20px" width="100px" />
                  </Box>
                </Flex>
              ))}
            </VStack>
          </Box>
        </>
      );
    }

    if (!orderDetail) {
      return null;
    }

    return <OrderDetail orderData={orderDetail} isModal onStatusChange={handleStatusChange} isAdmin={isAdmin} />;
  };

  return (
    <Box p={[4, 6, 8]} bg={bgColor} minH="100vh">
      <Flex justify="space-between" align="center" mb={8}>
        <Tooltip label="Geri Dön" placement="bottom" hasArrow>
          <Button
            leftIcon={<ChevronLeftIcon boxSize={8} />}
            onClick={() => navigate('/shop')}
            variant="ghost"
            bg="transparent"
            color={useColorModeValue('blue.400', textColorPrimary)}
            size="lg"
            borderRadius="full"
            transition="all 0.2s ease-in-out"
          >
            Geri Dön
          </Button>
        </Tooltip>
        <Heading
          size="xl"
          color={textColorPrimary}
          pb={2}
          borderBottomWidth="2px"
          borderColor={useColorModeValue('orange.200', '#FF6500')}
          letterSpacing="tight"
        >
          {isAdmin ? 'Siparişler' : 'Siparişlerim'}
        </Heading>
        <Box w="100px" /> {/* Spacer for alignment */}
      </Flex>

      <Box
        bg={cardBgColor}
        borderRadius="xl"
        boxShadow="lg"
        overflow="hidden"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <DataTable columns={columns} data={orders || []} usePagination />
      </Box>

      {/* Order Detail Modal */}
      <Modal isOpen={!!orderDetail} onClose={() => setOrderDetail(null)} size={{ base: 'full', md: '4xl' }} isCentered>
        <ModalOverlay />
        <ModalContent bg={bgColor} color={textColorPrimary} borderRadius="2xl" boxShadow="2xl" p={0} maxW="900px">
          <ModalCloseButton color={textColorPrimary} top={4} right={4} size="lg" />
          <Box p={{ base: 4, md: 10 }}>
            <ModalHeader p={0} mb={6} color={priceColor} fontWeight="bold" fontSize="2xl" letterSpacing="tight">
              Sipariş Detayı
            </ModalHeader>
            {renderModalContent()}
          </Box>
        </ModalContent>
      </Modal>
    </Box>
  );
};
